import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDateTime } from "@/lib/format";
import LoginHistoryModal from "@/components/LoginHistoryModal";
import { Users, ShieldCheck, AlertTriangle, Activity, Trophy, Clock, LineChart as LineChartIcon, ShieldAlert, ChevronDown, ChevronUp, History, Search, X } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

function StatCard({ icon: Icon, label, value, tone = "blue", testid }) {
  const toneMap = {
    blue: "from-blue-500 to-indigo-600 shadow-blue-500/30",
    green: "from-emerald-500 to-green-600 shadow-emerald-500/30",
    red: "from-red-500 to-rose-600 shadow-red-500/30",
    purple: "from-purple-500 to-fuchsia-600 shadow-purple-500/30",
    amber: "from-amber-500 to-orange-600 shadow-amber-500/30",
  };
  return (
    <div className="card-elevated p-5 flex items-center gap-4" data-testid={testid}>
      <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${toneMap[tone]} flex items-center justify-center shadow-lg`}>
        <Icon className="text-white" size={22} />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
        <div className="font-display font-bold text-2xl text-slate-900 mt-0.5">{value}</div>
      </div>
    </div>
  );
}

const PREVIEW_ROWS = 5;

// Bottone "Mostra tutti (N)" / "Comprimi" per tabelle con >PREVIEW_ROWS righe.
function ShowMoreButton({ total, expanded, onToggle, testid }) {
  if (total <= PREVIEW_ROWS) return null;
  return (
    <div className="flex justify-center mt-3">
      <button
        onClick={onToggle}
        className="text-xs px-4 py-1.5 rounded-lg border border-slate-200 hover:border-blue-400 hover:text-blue-600 text-slate-600 font-semibold flex items-center gap-1.5 transition"
        data-testid={testid}
      >
        {expanded ? (
          <>Comprimi <ChevronUp size={13} /></>
        ) : (
          <>Mostra tutti ({total}) <ChevronDown size={13} /></>
        )}
      </button>
    </div>
  );
}

export default function AdminAccessStats() {
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === "admin";
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyUser, setHistoryUser] = useState(null);
  const [q, setQ] = useState("");
  // Stato di espansione delle 5 tabelle (compresse a 5 righe di default)
  const [expanded, setExpanded] = useState({
    top: false, failed: false, recent: false, alerts: false, alertHistory: false,
  });
  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/access-stats?days=${days}`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (loading || !data) return <div className="text-slate-500">Caricamento…</div>;
  const raw = data;
  // Filtro case-insensitive su email / full_name / company_name (dove disponibile).
  const needle = q.trim().toLowerCase();
  const matches = (obj, keys) => {
    if (!needle) return true;
    return keys.some((k) => (obj?.[k] || "").toString().toLowerCase().includes(needle));
  };
  const top_users = raw.top_users.filter((u) => matches(u, ["email", "full_name", "company_name"]));
  const failed_recent = raw.failed_recent.filter((f) => matches(f, ["email"]));
  const recent_logins = raw.recent_logins.filter((r) => matches(r, ["email"]));
  const ip_alerts = (raw.ip_alerts || []).filter((a) => matches(a, ["email", "full_name", "company_name"]));
  const ip_alert_history = (raw.ip_alert_history || []).filter((h) => matches(h, ["email", "full_name", "company_name"]));
  const totals = raw.totals;
  const login_trend = raw.login_trend || [];
  const topRows = expanded.top ? top_users : top_users.slice(0, PREVIEW_ROWS);
  const failedRows = expanded.failed ? failed_recent : failed_recent.slice(0, PREVIEW_ROWS);
  const recentRows = expanded.recent ? recent_logins : recent_logins.slice(0, PREVIEW_ROWS);
  const alertRows = expanded.alerts ? ip_alerts : ip_alerts.slice(0, PREVIEW_ROWS);
  const alertHistoryRows = expanded.alertHistory ? ip_alert_history : ip_alert_history.slice(0, PREVIEW_ROWS);
  const trendData = login_trend.map((d) => {
    const dt = new Date(d.date + "T00:00:00Z");
    return {
      ...d,
      label: dt.toLocaleDateString("it-IT", { day: "2-digit", month: "short" }),
    };
  });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Statistiche accessi</h1>
          <p className="text-sm text-slate-500 mt-1">Monitoraggio utilizzo del portale — chi accede, quando e da dove.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filtra per email o azienda…"
              className="pl-8 pr-8 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-blue-500 w-64"
              data-testid="access-stats-search"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                data-testid="access-stats-search-clear"
                aria-label="Pulisci filtro"
              >
                <X size={12} />
              </button>
            )}
          </div>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="period-toggle">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)} className={`px-3 py-1.5 rounded-md text-xs font-semibold ${days===d?"bg-white shadow text-blue-600":"text-slate-500"}`} data-testid={`period-${d}`}>{d}g</button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard icon={ShieldCheck} label={`Accessi ok (${days}g)`} value={totals.success} tone="green" testid="stat-success" />
        <StatCard icon={Users} label="Utenti attivi" value={totals.active_users} tone="blue" testid="stat-active" />
        <StatCard icon={Activity} label="Ultime 24h" value={totals.last_24h_success} tone="purple" testid="stat-24h" />
        <StatCard icon={AlertTriangle} label={`Tentativi falliti (${days}g)`} value={totals.failed} tone="red" testid="stat-failed" />
        <StatCard icon={ShieldAlert} label="Alert IP attivi (24h)" value={totals.ip_alerts_active || 0} tone="amber" testid="stat-ip-alerts" />
      </div>

      <div className="card-elevated p-5" data-testid="login-trend-chart">
        <div className="flex items-center gap-2 mb-4">
          <LineChartIcon className="text-blue-500" size={18} />
          <div className="font-display font-semibold text-slate-900">Trend accessi ultimi {days} giorni</div>
        </div>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <LineChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="okStroke" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#0B4DFF" />
                  <stop offset="100%" stopColor="#7C3AED" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} width={30} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                labelStyle={{ fontWeight: 600 }}
                formatter={(v, name) => [v, name === "success" ? "Riusciti" : "Falliti"]}
              />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                formatter={(v) => (v === "success" ? "Accessi riusciti" : "Tentativi falliti")}
              />
              <Line
                type="monotone"
                dataKey="success"
                stroke="url(#okStroke)"
                strokeWidth={2.5}
                dot={{ r: 3, fill: "#0B4DFF" }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="failed"
                stroke="#ef4444"
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={{ r: 2, fill: "#ef4444" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-elevated p-5">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="text-amber-500" size={18} />
            <div className="font-display font-semibold text-slate-900">Top utenti per accessi</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  <th className="text-left py-2">#</th>
                  <th className="text-left py-2">Utente</th>
                  <th className="text-right py-2">Accessi</th>
                  <th className="text-right py-2">Ultimo</th>
                </tr>
              </thead>
              <tbody>
                {top_users.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-400 text-sm">Nessun dato</td></tr>}
                {topRows.map((u, i) => (
                  <tr key={u.user_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`top-user-${i}`}>
                    <td className="py-2 text-sm text-slate-400 font-mono">{i + 1}</td>
                    <td className="py-2 text-sm">
                      <div className="font-semibold text-slate-900">{u.full_name || u.email}</div>
                      <div className="text-[11px] text-slate-500">{u.company_name || "—"} · <span className="uppercase text-[10px]">{u.role}</span></div>
                    </td>
                    <td className="py-2 text-sm text-right font-mono-nums font-bold text-blue-600">{u.count}</td>
                    <td className="py-2 text-[11px] text-right text-slate-500">{fmtDateTime(u.last_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ShowMoreButton total={top_users.length} expanded={expanded.top} onToggle={() => toggle("top")} testid="toggle-top-users" />
        </div>

        <div className="card-elevated p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="text-red-500" size={18} />
            <div className="font-display font-semibold text-slate-900">Tentativi falliti recenti</div>
          </div>
          <div className="overflow-y-auto max-h-96">
            <table className="w-full">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100 sticky top-0 bg-white">
                  <th className="text-left py-2">Email tentata</th>
                  <th className="text-left py-2">IP</th>
                  <th className="text-left py-2">Motivo</th>
                  <th className="text-right py-2">Data/ora</th>
                </tr>
              </thead>
              <tbody>
                {failed_recent.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-400 text-sm">Nessun tentativo fallito 🎉</td></tr>}
                {failedRows.map((f, i) => (
                  <tr key={i} className="border-b border-slate-50" data-testid={`failed-${i}`}>
                    <td className="py-2 text-xs font-mono">{f.email || "—"}</td>
                    <td className="py-2 text-xs font-mono text-slate-500">{f.ip || "—"}</td>
                    <td className="py-2 text-xs"><span className="badge-neg">{f.reason || "unknown"}</span></td>
                    <td className="py-2 text-[11px] text-right text-slate-500">{fmtDateTime(f.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ShowMoreButton total={failed_recent.length} expanded={expanded.failed} onToggle={() => toggle("failed")} testid="toggle-failed" />
        </div>
      </div>

      <div className="card-elevated p-5">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="text-blue-500" size={18} />
          <div className="font-display font-semibold text-slate-900">Accessi riusciti recenti</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="text-left py-2">Email</th>
                <th className="text-left py-2">Ruolo</th>
                <th className="text-left py-2">IP</th>
                <th className="text-left py-2">Browser/OS</th>
                <th className="text-right py-2">Data/ora</th>
              </tr>
            </thead>
            <tbody>
              {recent_logins.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-400 text-sm">Nessun accesso registrato</td></tr>}
              {recentRows.map((r, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`recent-${i}`}>
                  <td className="py-2 text-xs font-mono">{r.email}</td>
                  <td className="py-2 text-[10px] uppercase font-semibold text-slate-500">{r.role}</td>
                  <td className="py-2 text-xs font-mono text-slate-500">{r.ip || "—"}</td>
                  <td className="py-2 text-[11px] text-slate-500 max-w-xs truncate" title={r.user_agent}>{(r.user_agent || "").split(")")[0].slice(0, 60) || "—"}</td>
                  <td className="py-2 text-[11px] text-right text-slate-500">{fmtDateTime(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ShowMoreButton total={recent_logins.length} expanded={expanded.recent} onToggle={() => toggle("recent")} testid="toggle-recent" />
      </div>

      <div className={`card-elevated p-5 ${ip_alerts.length > 0 ? "border-2 border-amber-300 bg-gradient-to-br from-amber-50/50 to-white" : ""}`} data-testid="ip-alerts-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ShieldAlert className={ip_alerts.length > 0 ? "text-amber-600" : "text-slate-400"} size={18} />
            <div className="font-display font-semibold text-slate-900">
              Alert condivisione credenziali (&gt;3 IP diversi in 24h)
            </div>
            {ip_alerts.length > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold" data-testid="ip-alerts-badge">
                {ip_alerts.length}
              </span>
            )}
          </div>
        </div>
        {ip_alerts.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center" data-testid="ip-alerts-empty">
            Nessun account con accessi sospetti nelle ultime 24 ore.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left py-2">Utente</th>
                  <th className="text-left py-2">Azienda</th>
                  <th className="text-right py-2">IP distinti (24h)</th>
                  <th className="text-right py-2">Login (24h)</th>
                  <th className="text-right py-2">Ultimo accesso</th>
                  <th className="text-right py-2">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {alertRows.map((a) => (
                  <tr key={a.user_id} className="border-b border-slate-100 hover:bg-white" data-testid={`ip-alert-row-${a.user_id}`}>
                    <td className="py-2.5 text-sm">
                      <div className="font-semibold text-slate-900">{a.full_name || a.email}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{a.email}</div>
                    </td>
                    <td className="py-2.5 text-sm text-slate-700">{a.company_name || "—"}</td>
                    <td className="py-2.5 text-right">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold font-mono-nums">
                        {a.distinct_ips_24h}
                      </span>
                    </td>
                    <td className="py-2.5 text-sm text-right font-mono-nums text-slate-600">{a.login_count_24h || "—"}</td>
                    <td className="py-2.5 text-[11px] text-right text-slate-500">{fmtDateTime(a.last_login_at)}</td>
                    <td className="py-2.5 text-right">
                      {isAdmin ? (
                      <button
                        onClick={() => setHistoryUser({ id: a.user_id, email: a.email, full_name: a.full_name })}
                        className="text-xs px-3 py-1.5 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-100 font-semibold"
                        data-testid={`open-ip-alert-${a.user_id}`}
                      >
                        Dettagli
                      </button>
                      ) : (
                        <span className="text-[10px] text-slate-400 italic">solo admin</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ShowMoreButton total={ip_alerts.length} expanded={expanded.alerts} onToggle={() => toggle("alerts")} testid="toggle-alerts" />
          </div>
        )}
      </div>

      {historyUser && (
        <LoginHistoryModal user={historyUser} onClose={() => { setHistoryUser(null); load(); }} />
      )}

      <div className="card-elevated p-5" data-testid="ip-alert-history-panel">
        <div className="flex items-center gap-2 mb-4">
          <History className="text-slate-500" size={18} />
          <div className="font-display font-semibold text-slate-900">Storico Alert IP (ultimi 7 giorni)</div>
          {ip_alert_history.length > 0 && (
            <span className="ml-2 px-2 py-0.5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold" data-testid="ip-alert-history-badge">
              {ip_alert_history.length}
            </span>
          )}
        </div>
        {ip_alert_history.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center" data-testid="ip-alert-history-empty">
            Nessun alert registrato negli ultimi 7 giorni.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="text-left py-2">Utente</th>
                    <th className="text-left py-2">Azienda</th>
                    <th className="text-right py-2">IP distinti</th>
                    <th className="text-left py-2">Ultimo IP</th>
                    <th className="text-right py-2">Alert registrato</th>
                  </tr>
                </thead>
                <tbody>
                  {alertHistoryRows.map((h, i) => (
                    <tr key={`${h.user_id}-${h.created_at}-${i}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ip-alert-history-row-${i}`}>
                      <td className="py-2.5 text-sm">
                        <div className="font-semibold text-slate-900">{h.full_name || h.email}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{h.email}</div>
                      </td>
                      <td className="py-2.5 text-sm text-slate-700">{h.company_name || "—"}</td>
                      <td className="py-2.5 text-right">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-xs font-bold font-mono-nums border border-amber-200">
                          {h.distinct_ips_24h || "—"}
                        </span>
                      </td>
                      <td className="py-2.5 text-xs font-mono text-slate-600">{h.latest_ip || "—"}</td>
                      <td className="py-2.5 text-[11px] text-right text-slate-500">{fmtDateTime(h.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ShowMoreButton total={ip_alert_history.length} expanded={expanded.alertHistory} onToggle={() => toggle("alertHistory")} testid="toggle-alert-history" />
          </>
        )}
      </div>
    </div>
  );
}
